/**
 * S-52 報告書兼請求書（別記3）一覧・作成／S-54 助成金領収書（別記4）一覧・作成
 * kintone: 監視経費_報告書兼請求書・監視経費_領収書（一覧画面の「レコード作成」「検索」）
 */
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { FilePlus2, FileStack, Search } from 'lucide-react';
import {
  Button,
  DataTable,
  useConfirm,
  DateField,
  EmptyState,
  ErrorState,
  FormField,
  FormGrid,
  PageHeader,
  Panel,
  useToast,
  type Column,
} from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { useRequestReportJob } from '../common/api';
import { Money, OperatorSelect, VesselExcludePicker } from '../common/components';
import { useOperatorScope } from '../common/useOperatorScope';
import { useGenerateStatements, useStatements, type StatementListItem, type StatementType } from './api';

type FormValues = { businessOperatorId: number | null; from: string; to: string; excludeVesselIds: number[]; receiptDate?: string };

/** 領収日付が未入力のまま作成するときの確認（kintone 検証指摘：領収日付が空白の場合はワーニング） */
export const RECEIPT_DATE_WARNING = '領収日付が入力されていません。帳票の日付欄は空欄（手書き用の「年 月 日」）で出力されます。このまま作成しますか？';

const TEXT: Record<StatementType, { title: string; eyebrow: string; desc: string; path: string; reportId: 'R-09' | 'R-10'; dateLabel: string }> = {
  請求書: {
    title: '報告書兼請求書（別記3）',
    eyebrow: '請求 漁業者→漁業協同組合',
    desc: '調査日の期間と船を指定して、速報から監視経費（支払・購入）報告書兼請求書を船ごとに作成します。',
    path: 'invoices',
    reportId: 'R-09',
    dateLabel: '請求日',
  },
  領収書: {
    title: '助成金領収書（別記4）',
    eyebrow: '支払い 漁業協同組合→漁業者',
    desc: '調査日の期間と船を指定して、速報から監視経費（支払・購入）助成金領収書を船ごとに作成します。',
    path: 'receipts',
    reportId: 'R-10',
    dateLabel: '領収日付',
  },
};

function useCriteria(): [FormValues, (v: FormValues) => void] {
  const [params, setParams] = useSearchParams();
  const v = useMemo<FormValues>(
    () => ({
      businessOperatorId: params.get('businessOperatorId') ? Number(params.get('businessOperatorId')) : null,
      from: params.get('from') ?? '',
      to: params.get('to') ?? '',
      excludeVesselIds: params.getAll('excludeVesselIds').map(Number).filter(Boolean),
    }),
    [params],
  );
  const set = (x: FormValues) => {
    const next = new URLSearchParams();
    if (x.businessOperatorId != null) next.set('businessOperatorId', String(x.businessOperatorId));
    if (x.from) next.set('from', x.from);
    if (x.to) next.set('to', x.to);
    x.excludeVesselIds.forEach((id) => next.append('excludeVesselIds', String(id)));
    setParams(next, { replace: true });
  };
  return [v, set];
}

export function StatementListPage({ type }: { type: StatementType }) {
  const t = TEXT[type];
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { year, yearPath } = useFiscalYear();
  const { isCoop, fixedOperatorId } = useOperatorScope();
  const [criteria, setCriteria] = useCriteria();
  const isReceipt = type === '領収書';
  const operatorId = isCoop ? fixedOperatorId : criteria.businessOperatorId;
  const list = useStatements(type, year, { businessOperatorId: operatorId, from: criteria.from, to: criteria.to, excludeVesselIds: criteria.excludeVesselIds }, operatorId != null || !isCoop);
  const generate = useGenerateStatements();
  const job = useRequestReportJob();
  const { control, handleSubmit, watch, register, formState } = useForm<FormValues>({ values: { ...criteria, businessOperatorId: operatorId } });
  const formOperator = watch('businessOperatorId');

  const onCreate = handleSubmit(async (v) => {
    if (!isCoop && v.businessOperatorId == null) return toast.error('事業実施者IDは必須です');
    if (!v.from || !v.to) return toast.error('調査日は必須です');
    if (isReceipt && !v.receiptDate && !(await confirm({ title: '領収日付が未入力です', message: RECEIPT_DATE_WARNING, confirmLabel: '作成する' }))) return;
    try {
      const res = await generate.mutateAsync({
        type,
        fiscalYear: year,
        businessOperatorId: isCoop ? null : v.businessOperatorId,
        from: v.from,
        to: v.to,
        excludeVesselIds: v.excludeVesselIds,
        ...(isReceipt ? { receiptDate: v.receiptDate || null } : {}),
      });
      toast.success('帳票作成が完了しました。', `${res.ids.length} 件作成しました`);
      setCriteria(v);
    } catch (e) {
      toast.error('帳票を作成できませんでした', errorMessage(e));
    }
  });

  const onBulk = async () => {
    try {
      await job.mutateAsync({
        reportId: t.reportId,
        fiscalYear: year,
        params: { type, fiscalYear: year, businessOperatorId: operatorId ?? undefined, from: criteria.from || undefined, to: criteria.to || undefined, excludeVesselIds: criteria.excludeVesselIds },
      });
      toast.success('帳票一括作成を依頼しました', '「帳票一括作成ジョブ」画面で作成状況を確認し、ZIP をダウンロードしてください。');
      navigate(yearPath('report-jobs'));
    } catch (e) {
      toast.error('一括出力を依頼できませんでした', errorMessage(e));
    }
  };

  const columns: Column<StatementListItem>[] = [
    { key: 'rosterNo', header: '名簿No', numeric: true, width: 84, sortable: true },
    { key: 'vesselName', header: '船名', sortable: true, width: 170, nowrap: true },
    ...(!isCoop ? [{ key: 'businessOperatorName', header: '事業実施者', width: 170, sortable: true } satisfies Column<StatementListItem>] : []),
    { key: 'period', header: '調査日（作成条件）', width: 205, nowrap: true, render: (r) => <span className="bs-num">{r.periodFrom}〜{r.periodTo}</span> },
    { key: 'claimantName', header: '氏名', width: 95, nowrap: true },
    { key: type === '請求書' ? 'billingDate' : 'receiptDate', header: t.dateLabel, width: 110, sortable: true, render: (r) => <span className="bs-num">{(type === '請求書' ? r.billingDate : r.receiptDate) ?? '—'}</span> },
    { key: 'taxableAmount', header: '計（課税対象額）', width: 130, align: 'right', render: (r) => <Money value={r.taxableAmount} /> },
    { key: 'taxAmount', header: '消費税', width: 100, align: 'right', render: (r) => <Money value={r.taxAmount} /> },
    { key: 'totalAmount', header: type === '請求書' ? '請求金額' : '金額', width: 120, align: 'right', sortable: true, render: (r) => <Money value={r.totalAmount} /> },
  ];

  return (
    <>
      <PageHeader eyebrow={t.eyebrow} title={t.title} description={t.desc} />
      <Panel title="帳票作成・検索条件" description="作成は「事業実施者・調査日（必須）・除外船」で行います。検索は同じ条件で作成済みの帳票を表示します。">
        <form
          className="bs-exp-stack"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit((v) => setCriteria(v))();
          }}
        >
          <FormGrid columns={3}>
            <FormField label="事業実施者" required htmlFor="stmt-operator">
              <Controller control={control} name="businessOperatorId" render={({ field }) => <OperatorSelect id="stmt-operator" year={year} value={field.value} onChange={field.onChange} />} />
            </FormField>
            <FormField label="調査日" required span={2} hint="作成：調査期間が範囲と重なる速報が対象／検索：調査終了日 ≧ FROM かつ 調査開始日 ≦ TO">
              <span className="bs-exp-range">
                <DateField {...register('from')} aria-label="調査日FROM" />
                <span>〜</span>
                <DateField {...register('to')} aria-label="調査日TO" />
              </span>
            </FormField>
            {isReceipt && (
              <FormField label="領収日付" hint="未入力の場合は手書き用の「年 月 日」で出力します（詳細画面でも変更できます）">
                <DateField {...register('receiptDate')} aria-label="領収日付" showWareki />
              </FormField>
            )}
          </FormGrid>
          <Controller
            control={control}
            name="excludeVesselIds"
            render={({ field }) => <VesselExcludePicker year={year} operatorId={isCoop ? fixedOperatorId : formOperator} value={field.value} onChange={field.onChange} />}
          />
          <div className="bs-exp-row bs-exp-row--end">
            <Button type="submit" icon={<Search size={16} />} loading={list.isFetching && formState.isSubmitted}>
              検索
            </Button>
            <Button variant="primary" icon={<FilePlus2 size={16} />} onClick={() => void onCreate()} loading={generate.isPending}>
              帳票作成
            </Button>
          </div>
        </form>
      </Panel>

      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <DataTable
          caption={`${t.title} 作成済み一覧`}
          columns={columns}
          rows={list.data?.items ?? []}
          rowKey={(r) => r.id}
          loading={list.isPending && list.fetchStatus !== 'idle'}
          pagination={{ pageSize: 50 }}
          onRowClick={(r) => navigate(yearPath(`${t.path}/${r.id}`))}
          toolbar={
            <Button size="sm" icon={<FileStack size={15} />} onClick={() => void onBulk()} loading={job.isPending} disabled={!list.data?.items.length}>
              一括出力（ZIP）
            </Button>
          }
          empty={<EmptyState compact icon={<FileStack />} title="作成済みの帳票はありません" description="条件を指定して「帳票作成」を押してください。" />}
        />
      )}
    </>
  );
}
